/**
 * Spotlight recent selections (localStorage). Cap 8, dedupe by path.
 */

export const SPOTLIGHT_RECENT_STORAGE_KEY = 'spotlight.recent.v1';
export const SPOTLIGHT_RECENT_LIMIT = 8;

export type SpotlightRecentKind = 'action' | 'hit' | 'ctx';

export type SpotlightRecentItem = {
  id: string;
  path: string;
  label: string;
  kind: SpotlightRecentKind;
  /** Dialog open payload kept off the URL. */
  state?: object;
  at: number;
};

const isRecentItem = (value: unknown): value is SpotlightRecentItem => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.path === 'string' &&
    v.path.length > 0 &&
    typeof v.label === 'string' &&
    (v.kind === 'action' || v.kind === 'hit' || v.kind === 'ctx') &&
    typeof v.at === 'number'
  );
};

export const readRecent = (
  storage: Pick<Storage, 'getItem'> = localStorage,
): SpotlightRecentItem[] => {
  try {
    const raw = storage.getItem(SPOTLIGHT_RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentItem).slice(0, SPOTLIGHT_RECENT_LIMIT);
  } catch {
    return [];
  }
};

export const writeRecent = (
  items: SpotlightRecentItem[],
  storage: Pick<Storage, 'setItem'> = localStorage,
): void => {
  storage.setItem(
    SPOTLIGHT_RECENT_STORAGE_KEY,
    JSON.stringify(items.slice(0, SPOTLIGHT_RECENT_LIMIT)),
  );
};

/** Insert/move item to front; dedupe by path; cap limit. */
export const pushRecent = (
  item: Omit<SpotlightRecentItem, 'at'> & { at?: number },
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): SpotlightRecentItem[] => {
  const nextItem: SpotlightRecentItem = {
    id: item.id,
    path: item.path,
    label: item.label,
    kind: item.kind,
    ...(item.state ? { state: item.state } : {}),
    at: item.at ?? Date.now(),
  };
  if (!nextItem.path) {
    return readRecent(storage);
  }
  const existing = readRecent(storage).filter((r) => r.path !== nextItem.path);
  const next = [nextItem, ...existing].slice(0, SPOTLIGHT_RECENT_LIMIT);
  writeRecent(next, storage);
  return next;
};
